import { useState, useEffect, useRef } from "react";
import { useRazorpay } from "react-razorpay";
import { toast } from "sonner";
import { Loader2, CheckCircle2, Instagram, Volume2, VolumeX, MessageCircle, Lock, Shield } from "lucide-react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "../components/ui/accordion";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../components/ui/dialog";
import { Button } from "../components/ui/button";
import { createDonationOrder, verifyDonation } from "../lib/api";
import ImpactCounter from "../components/ImpactCounter";
import TopDonors from "../components/TopDonors";

const AMOUNTS = [250, 500, 1000, 1500, 2500, 5000];

const IMPACT_TIER_HERO = {
  100: "Helps provide a nutritious meal for a hungry stray",
  250: "Supports basic medical treatment and first aid for a rescued animal",
  500: "Covers vaccinations and essential healthcare for an animal in need",
  1000: "Helps fund rescue operations, rehabilitation, and temporary shelter",
  2500: "Gives a rescued animal comprehensive care, treatment, and the opportunity to find a loving forever home",
  5000: "Sponsors a full month of food, medical care, and safe shelter for a rescued animal at our sanctuary",
};

const HERO_THUMBS = [
  "/flood/hero3.png",
  "/flood/hero2.png",
];

const CALLOUT_THUMBS = [
  "/flood/f4.jpg",
  "/flood/f2.jpg",
  "/flood/f5.jpg",
  "/flood/f1.jpg",
];

const WORK_REELS = [
  "/reels/reel1.mp4",
  "/reels/reel2.mp4",
  "/reels/reel3.mp4",
  "/reels/reel4.mp4",
];

const FAQS = [
  {
    q: "1. How will my donation be used?",
    a: "Your donation helps fund animal rescues, emergency medical treatment, food, shelter, rehabilitation, and other welfare initiatives for animals in need.",
  },
  {
    q: "2. Is my donation secure?",
    a: "Yes. All donations are processed through Razorpay, a secure payment gateway with industry-standard encryption to ensure your information is protected.",
  },
  {
    q: "3. Can I donate any amount?",
    a: "Yes, every contribution makes a difference. Donations start from ₹50, and every rupee helps us provide care and support to vulnerable animals.",
  },
  {
    q: "4. How else can I support Pawsomeland Foundation?",
    a: "Apart from donating, you can volunteer, spread awareness, foster or adopt rescued animals, and share our mission with your family and friends.",
  },
];

export default function Give() {
  const { Razorpay } = useRazorpay();
  const [amount, setAmount] = useState(500);
  const [donor, setDonor] = useState({ name: "", phone: "", email: "" });
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(null);
  const [impactKey, setImpactKey] = useState(0);
  const [monthlyModalOpen, setMonthlyModalOpen] = useState(false);
  const [monthlyForm, setMonthlyForm] = useState({ name: "", email: "", phone: "" });

  const updateDonor = (k, v) => setDonor((s) => ({ ...s, [k]: v }));

  const handleQuickDonate = (fixedAmount, isRecurring = false) => {
    setAmount(fixedAmount);
    if (!donor.name?.trim() || !donor.email?.trim()) {
      // For monthly: open a compact modal in-place so user never leaves the section
      if (isRecurring) {
        setMonthlyForm({ name: donor.name || "", email: donor.email || "", phone: donor.phone || "" });
        setMonthlyModalOpen(true);
        return;
      }
      // For one-time: fall back to scrolling to the main form
      const formEl = document.getElementById("give-form");
      formEl?.scrollIntoView({ behavior: "smooth", block: "start" });
      setTimeout(() => {
        const target = !donor.name?.trim() ? "give-name-input" : "give-email-input";
        document.querySelector(`[data-testid="${target}"]`)?.focus();
      }, 600);
      toast("One quick step — enter your name & email to continue 🐾", { duration: 4000 });
      return;
    }
    handleDonate(fixedAmount, isRecurring);
  };

  const submitMonthlyModal = async () => {
    const m = monthlyForm;
    if (!m.name?.trim()) return toast.error("Please share your name");
    if (!m.email?.trim()) return toast.error("Please share your email");
    // Sync into main donor state so the rest of the page reflects it
    setDonor({ name: m.name.trim(), email: m.email.trim(), phone: m.phone?.trim() || "" });
    setMonthlyModalOpen(false);
    await handleDonate(3000, true, { name: m.name.trim(), email: m.email.trim(), phone: m.phone?.trim() || "" });
  };

  const handleDonate = async (overrideAmount, isRecurring = false, donorOverride) => {
    const amt = typeof overrideAmount === "number" ? overrideAmount : amount;
    const d = donorOverride || donor;
    if (!amt || amt < 50) {
      toast.error("Please enter at least ₹50");
      return;
    }
    if (!d.name?.trim()) {
      toast.error("Please share your name");
      return;
    }
    if (!d.email?.trim()) {
      toast.error("Please share your email");
      return;
    }
    setLoading(true);

    if (typeof window !== "undefined" && typeof window.fbq === "function") {
      window.fbq("track", "InitiateCheckout", {
        value: amt, currency: "INR", content_name: "Donation", content_category: "Donation",
      });
    }

    try {
      const order = await createDonationOrder({
        amount: amt,
        name: d.name.trim(),
        email: d.email.trim(),
        phone: d.phone?.trim() || null,
        message: null,
        anonymous: false,
        recurring: isRecurring,
        campaign: "flood_relief",
      });

      if (order.mock) {
        await verifyDonation({
          donation_id: order.donation_id,
          razorpay_order_id: order.order_id,
          razorpay_payment_id: `mock_pay_${Date.now()}`,
          razorpay_signature: "mock_signature",
        });
        if (typeof window !== "undefined" && typeof window.fbq === "function") {
          window.fbq("track", "Purchase", {
            value: amt, currency: "INR", content_name: "Donation", content_category: "Donation",
          });
        }
        setSuccess({ amount: amt, mock: true, recurring: false });
        setImpactKey((k) => k + 1);
        setLoading(false);
        return;
      }

      const options = {
        key: order.razorpay_key_id,
        name: "Pawsomeland Foundation",
        description: order.is_recurring ? "Monthly donation" : "Support rescued animals",
        prefill: { name: d.name, email: d.email, contact: d.phone || "" },
        theme: { color: "#C85A40" },
        handler: async (response) => {
          try {
            await verifyDonation({
              donation_id: order.donation_id,
              razorpay_order_id: response.razorpay_order_id || null,
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_signature: response.razorpay_signature,
              razorpay_subscription_id: response.razorpay_subscription_id || null,
            });
            if (typeof window !== "undefined" && typeof window.fbq === "function") {
              window.fbq("track", "Purchase", {
                value: amt, currency: "INR", content_name: "Donation", content_category: "Donation",
              });
            }
            setSuccess({ amount: amt, mock: false, recurring: order.is_recurring });
            setImpactKey((k) => k + 1);
          } catch {
            toast.error("Payment verification failed. Please contact us.");
          } finally {
            setLoading(false);
          }
        },
        modal: { ondismiss: () => setLoading(false) },
      };

      // For subscriptions, pass subscription_id instead of order_id + amount
      if (order.is_recurring) {
        options.subscription_id = order.subscription_id;
      } else {
        options.order_id = order.order_id;
        options.amount = order.amount;
        options.currency = order.currency;
      }

      const RazorpayCtor = Razorpay || (typeof window !== "undefined" && window.Razorpay);
      if (!RazorpayCtor) {
        toast.error("Payment SDK is still loading. Please try again in a moment.");
        setLoading(false);
        return;
      }
      const rzp = new RazorpayCtor(options);
      rzp.on("payment.failed", () => { toast.error("Payment failed. Please try again."); setLoading(false); });
      rzp.open();
    } catch (err) {
      console.error("Donation error:", err);
      const detail = err?.response?.data?.detail;
      const msg = typeof detail === "string"
        ? detail
        : Array.isArray(detail)
          ? (detail[0]?.msg || "Please check the form and try again.")
          : (err?.message || "Could not start donation.");
      toast.error(msg);
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-bone text-ink pt-4" data-testid="give-page">
      {/* HERO with inline form */}
      <section className="max-w-7xl mx-auto px-6 lg:px-10 py-12 lg:py-16">
        <div className="grid lg:grid-cols-2 gap-10 lg:gap-16 items-start">
          {/* LEFT — headline + thumbs */}
          <div className="space-y-8">
            <div className="space-y-5">
              <h1
                className="text-4xl sm:text-5xl lg:text-6xl text-forest leading-[1.05] tracking-tight"
                style={{ fontFamily: "'Nunito', sans-serif", fontWeight: 900 }}
                data-testid="give-headline"
              >
                <span className="block">The flood came<br className="hidden sm:block"/> <span className="sm:hidden"> </span>without warning.</span>
                <span className="block text-terracotta mt-2">300+ lives have<br className="hidden sm:block"/> <span className="sm:hidden"> </span>nowhere to run!</span>
              </h1>

              <div className="grid grid-cols-2 gap-3" data-testid="give-hero-images">
                {HERO_THUMBS.map((src) => (
                  <div key={src} className="aspect-[3/4] rounded-xl overflow-hidden bg-bone-soft shadow-[0_10px_25px_rgba(30,59,39,0.15)]">
                    <img src={src} alt="Rescued animals during the flood at Pawsomeland Foundation" loading="lazy" className="w-full h-full object-cover"/>
                  </div>
                ))}
              </div>
              <p className="text-xs text-ink/60 italic -mt-2">Photos from our shelter, this week.</p>

              <div className="space-y-3 text-base lg:text-lg text-ink/80 leading-relaxed max-w-xl">
                <p>Today, the river burst its banks and swallowed our one-acre shelter whole. Water everywhere. Beds floating. Fences underwater.</p>
                <p>Our 300+ rescued dogs — the paralysed, the elderly, the nervous new arrivals — huddled together as the flood rose around them.</p>
                <p>Only by God's grace, we had built the shelter <strong>raised above the ground</strong>. The water lapped at the edges but never touched their sleeping area.</p>
                <p>They are safe. But the land is destroyed — fencing gone, walkways washed out, food stores ruined, mud two feet deep everywhere.</p>
                <p className="text-forest font-semibold">
                  Help us rebuild before the next rain. <span className="text-terracotta">❤</span>
                </p>
              </div>
            </div>

            <div className="bg-white rounded-2xl border border-bone-soft p-5 lg:p-6 shadow-[0_15px_40px_rgba(30,59,39,0.10)]" data-testid="give-impact-callout">
              <div className="flex items-center justify-between mb-4 gap-3">
                <img src="/logo-horizontal.png" alt="" className="h-8 w-auto shrink-0"/>
                <p className="font-serif text-lg lg:text-xl text-forest text-right leading-tight">
                  Your <span className="text-terracotta font-medium">₹{amount.toLocaleString("en-IN")}</span> can rebuild <em className="italic">what the flood took</em>
                </p>
              </div>
              <div className="grid grid-cols-2 gap-3">
                {CALLOUT_THUMBS.map((src) => (
                  <div key={src} className="aspect-[3/4] rounded-xl overflow-hidden bg-bone-soft shadow-[0_10px_25px_rgba(30,59,39,0.15)]">
                    <img src={src} alt="Rescued animals during the flood at Pawsomeland Foundation" loading="lazy" className="w-full h-full object-cover"/>
                  </div>
                ))}
              </div>
              <p className="text-xs text-ink/60 text-center mt-4 italic">Photos from our shelter, this week.</p>
            </div>
          </div>

          {/* RIGHT — donation form card */}
          <div id="give-form" className="space-y-5 scroll-mt-28">
            <ImpactCounter slug="flood_relief" refreshKey={impactKey} />
            <div className="bg-white rounded-3xl border border-bone-soft p-6 lg:p-8 shadow-[0_15px_50px_rgba(30,59,39,0.08)]" data-testid="give-form-card">
            {/* Amount input */}
            <div className="mb-5">
              <input
                type="number"
                min={50}
                value={amount}
                onChange={(e) => setAmount(parseInt(e.target.value || "0", 10))}
                className="w-full h-14 px-5 rounded-full border-2 border-terracotta bg-bone/40 text-forest text-lg font-semibold outline-none focus:border-terracotta-deep transition-colors"
                data-testid="give-amount-input"
                placeholder="Enter amount"
              />
            </div>

            {/* Amount tier buttons */}
            <div className="grid grid-cols-3 gap-3 mb-6">
              {AMOUNTS.map((a) => (
                <button
                  key={a}
                  type="button"
                  onClick={() => setAmount(a)}
                  className={`h-12 rounded-full font-bold text-base border-2 transition-all ${
                    amount === a
                      ? "bg-terracotta text-bone border-terracotta shadow-[0_8px_20px_rgba(200,90,64,0.35)]"
                      : "bg-white text-terracotta border-terracotta/40 hover:border-terracotta hover:bg-terracotta/5"
                  }`}
                  data-testid={`give-amount-${a}`}
                >
                  ₹{a >= 1000 ? `${a % 1000 === 0 ? a / 1000 : (a / 1000).toFixed(1)}K` : a}
                </button>
              ))}
            </div>

            {/* Name */}
            <div className="mb-4">
              <label className="block text-sm font-bold text-forest mb-1.5">
                Name <span className="text-terracotta">*</span>
              </label>
              <input
                value={donor.name}
                onChange={(e) => updateDonor("name", e.target.value)}
                placeholder="Enter your name"
                className="w-full h-12 px-4 rounded-lg border border-bone-soft bg-white outline-none focus:border-terracotta transition-colors"
                data-testid="give-name-input"
              />
            </div>

            {/* Phone — optional */}
            <div className="mb-4">
              <label className="block text-sm font-bold text-forest mb-1.5">
                Phone <span className="text-ink/50 font-normal">(Optional)</span>
              </label>
              <input
                value={donor.phone}
                onChange={(e) => updateDonor("phone", e.target.value)}
                placeholder="Enter your phone number"
                inputMode="tel"
                className="w-full h-12 px-4 rounded-lg border border-bone-soft bg-white outline-none focus:border-terracotta transition-colors"
                data-testid="give-phone-input"
              />
            </div>

            {/* Email — required */}
            <div className="mb-6">
              <label className="block text-sm font-bold text-forest mb-1.5">
                Email <span className="text-terracotta">*</span>
              </label>
              <input
                type="email"
                value={donor.email}
                onChange={(e) => updateDonor("email", e.target.value)}
                placeholder="Enter your email address"
                className="w-full h-12 px-4 rounded-lg border border-bone-soft bg-white outline-none focus:border-terracotta transition-colors"
                data-testid="give-email-input"
              />
            </div>

            {/* Donate button */}
            <Button
              onClick={handleDonate}
              disabled={loading}
              className="w-full bg-terracotta hover:bg-terracotta-deep text-bone rounded-full h-14 text-base font-bold tracking-wide shadow-[0_15px_40px_rgba(200,90,64,0.35)]"
              data-testid="give-donate-button"
            >
              {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : "DONATE NOW"}
            </Button>

            <div className="flex items-center justify-center gap-2 mt-4 text-sm text-ink/65">
              <Lock className="w-4 h-4" strokeWidth={1.6}/>
              100% Secure Donation
            </div>

            {/* Payment methods trust bar */}
            <div className="mt-5 pt-5 border-t border-bone-soft" data-testid="give-payment-methods">
              <div className="text-[10px] uppercase tracking-[0.28em] text-ink/50 text-center mb-3 font-semibold">We accept</div>
              <div className="flex flex-wrap items-center justify-center gap-1.5 sm:gap-2">
                {/* GPay */}
                <div className="h-8 px-3 rounded-full bg-white border border-bone-soft flex items-center gap-0.5 text-[13px] font-semibold">
                  <span className="text-[#4285F4]">G</span>
                  <span className="text-[#EA4335]">P</span>
                  <span className="text-[#FBBC04]">a</span>
                  <span className="text-[#34A853]">y</span>
                </div>
                {/* PhonePe */}
                <div className="h-8 px-3 rounded-full bg-[#5F259F] text-white flex items-center text-[13px] font-bold">
                  Phone<span className="italic font-medium">Pe</span>
                </div>
                {/* Paytm */}
                <div className="h-8 px-3 rounded-full bg-[#00BAF2] text-white flex items-center text-[13px] font-bold">
                  Paytm
                </div>
                {/* UPI */}
                <div className="h-8 px-3 rounded-full bg-forest text-bone flex items-center text-[13px] font-bold tracking-wide">
                  UPI
                </div>
                {/* Cards */}
                <div className="h-8 px-3 rounded-full bg-white border border-bone-soft flex items-center gap-1.5 text-[13px] font-semibold text-ink/80">
                  <span className="inline-block w-4 h-3 rounded-sm bg-gradient-to-r from-[#1a1f71] via-[#1a1f71] to-[#f7b600] shrink-0"/>
                  Cards
                </div>
              </div>
              <div className="mt-3 flex items-center justify-center gap-1.5 text-xs text-ink/60">
                <Shield className="w-3.5 h-3.5 text-terracotta" strokeWidth={1.8}/>
                100% secure · Powered by <span className="font-bold text-[#072654]">Razorpay</span>
              </div>
            </div>

            <div className="text-center mt-4 pt-4 border-t border-bone-soft font-serif text-lg text-forest" data-testid="give-amount-display">
              Donation Amount: <span className="font-semibold">₹{amount.toLocaleString("en-IN")}</span>
            </div>
          </div>

          <TopDonors slug="flood_relief" refreshKey={impactKey} onQuickDonate={handleQuickDonate} loading={loading} />
          </div>
        </div>
      </section>

      {/* WHY DONATE */}
      <section className="max-w-5xl mx-auto px-6 lg:px-10 py-12 lg:py-16" data-testid="give-why-section">
        <h2 className="font-serif text-3xl lg:text-5xl font-light text-forest mb-6">
          Why <em className="italic text-terracotta">Donate?</em>
        </h2>
        <div className="space-y-5 text-lg text-ink/80 leading-relaxed">
          <p>
            Every day, abandoned, injured, and homeless animals struggle to survive on the streets. Your support helps provide life-saving medical care, nutritious food, safe shelter, and a second chance at a happy life.
          </p>
          <p>Every contribution, no matter the size, makes a real difference.</p>
          <ul className="space-y-2.5 pl-1">
            {Object.entries(IMPACT_TIER_HERO).map(([amt, desc]) => (
              <li key={amt} className="flex items-start gap-3">
                <span className="text-xl leading-none pt-0.5">🐾</span>
                <span><span className="font-semibold text-forest">₹{parseInt(amt).toLocaleString("en-IN")}</span> – {desc}.</span>
              </li>
            ))}
          </ul>
          <p className="font-serif italic text-xl text-forest pt-2">
            Every life matters. Every donation creates hope. Donate today and help change a life.
          </p>
        </div>
      </section>

      {/* SEE OUR WORK */}
      <section className="max-w-7xl mx-auto px-6 lg:px-10 py-12 lg:py-16" data-testid="give-work-section">
        <h2 className="font-serif text-3xl lg:text-5xl font-light text-forest mb-8">
          See Our Work in <em className="italic text-terracotta">Action</em>
        </h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 lg:gap-6">
          {WORK_REELS.map((src, i) => (
            <ReelPlayer key={src} src={src} testId={`give-work-${i}`} />
          ))}
        </div>
      </section>

      {/* HEART BEHIND */}
      <section className="max-w-6xl mx-auto px-6 lg:px-10 py-12 lg:py-16" data-testid="give-about-section">
        <h2 className="font-serif text-3xl lg:text-5xl font-light text-forest mb-8">
          The Heart Behind <em className="italic text-terracotta">Pawsomeland</em>
        </h2>
        <div className="grid lg:grid-cols-2 gap-10 lg:gap-14 items-start">
          <div className="rounded-3xl overflow-hidden shadow-[0_25px_60px_rgba(30,59,39,0.2)]">
            <img src="/vanny.jpg" alt="Pawsomeland founder with a rescued dog" className="w-full h-auto"/>
          </div>
          <div className="space-y-4 text-base lg:text-lg text-ink/80 leading-relaxed">
            <p>It started in 2019, during the lockdown, with a single bowl of food set out for a hungry dog in our neighbourhood. One bowl became ten. Ten became a hundred. Before we knew it, we were feeding the streets of Bhopal every single day.</p>
            <p>Since then, over 300 rescued animals have passed through our care. Some came in bleeding after accidents. Others were abandoned, paralysed, or too old for anyone to want. We took them in — every single one — and made them a home wherever we could find shelter.</p>
            <p>We've had to pack up and move five different times, carrying injured dogs from one temporary place to another. Every time, we started over. Every time, we swore we'd find them a permanent home. Today, on one acre of land in Bhopal, that home is finally being built.</p>
            <p>We are not a big NGO. We are a small team of people who couldn't look away — funded almost entirely by kind humans like you. Your donation isn't charity. It's the reason a paralysed dog wakes up warm tomorrow, and a hungry pup gets a meal tonight.</p>
            <p className="font-serif italic text-xl lg:text-2xl text-forest pt-2 leading-snug">
              Help us care for 300+ rescued dogs and build Bhopal's first cage-free shelter.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 pt-3" data-testid="give-about-ctas">
              <a
                href="https://www.instagram.com/the.pawsome.land/"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center justify-center gap-2.5 flex-1 bg-gradient-to-tr from-[#feda75] via-[#d62976] to-[#4f5bd5] hover:opacity-90 text-white rounded-full h-14 px-6 font-semibold text-base tracking-wide transition-opacity shadow-[0_10px_28px_rgba(214,41,118,0.35)]"
                data-testid="give-about-ig-cta"
              >
                <Instagram className="w-5 h-5" strokeWidth={2}/>
                Follow on Instagram
              </a>
              <a
                href="https://chat.whatsapp.com/KTPYodo4NTV1xwPCBgRcFt"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center justify-center gap-2.5 flex-1 bg-forest hover:bg-forest/90 text-bone rounded-full h-14 px-6 font-semibold text-base tracking-wide transition-colors shadow-[0_10px_28px_rgba(30,59,39,0.30)]"
                data-testid="give-about-wa-cta"
              >
                <MessageCircle className="w-5 h-5" strokeWidth={2}/>
                Join our WhatsApp community
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* FAQs */}
      <section className="max-w-4xl mx-auto px-6 lg:px-10 py-12 lg:py-16" data-testid="give-faq-section">
        <h2 className="font-serif text-3xl lg:text-5xl font-light text-forest mb-8">FAQs</h2>
        <Accordion type="single" collapsible className="space-y-3">
          {FAQS.map((f, i) => (
            <AccordionItem key={i} value={`f${i}`} className="bg-white rounded-2xl border border-bone-soft px-6 overflow-hidden" data-testid={`give-faq-${i}`}>
              <AccordionTrigger className="text-left font-semibold text-forest text-base lg:text-lg hover:no-underline">
                {f.q}
              </AccordionTrigger>
              <AccordionContent className="text-ink/75 leading-relaxed text-base pb-5">
                {f.a}
              </AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </section>

      {/* Monthly Quick Details Dialog */}
      <Dialog open={monthlyModalOpen} onOpenChange={setMonthlyModalOpen}>
        <DialogContent data-testid="monthly-dialog" className="bg-bone border-bone-soft max-w-md">
          <DialogHeader>
            <div className="w-11 h-11 rounded-full bg-terracotta/15 flex items-center justify-center mb-2">
              <span className="text-terracotta text-lg">❤</span>
            </div>
            <DialogTitle className="font-serif text-2xl text-forest font-light leading-tight">
              Become a monthly rescuer — ₹3,000/mo
            </DialogTitle>
            <DialogDescription className="text-ink/70 text-sm leading-relaxed">
              Monthly donors are the reason our 300+ dogs sleep safe every night. Your ₹3,000 arrives like clockwork — food, medicine, salaries for our caretakers — no panic fundraising, no missed vaccinations. You become the <strong className="text-forest">quiet family</strong> that keeps this shelter alive between disasters.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 pt-1">
            <input
              type="text"
              placeholder="Your name"
              value={monthlyForm.name}
              onChange={(e) => setMonthlyForm((s) => ({ ...s, name: e.target.value }))}
              className="w-full h-12 px-4 rounded-xl bg-white border border-bone-soft focus:border-forest focus:outline-none text-ink placeholder:text-ink/40 text-sm"
              data-testid="monthly-name-input"
            />
            <input
              type="email"
              placeholder="Your email"
              value={monthlyForm.email}
              onChange={(e) => setMonthlyForm((s) => ({ ...s, email: e.target.value }))}
              className="w-full h-12 px-4 rounded-xl bg-white border border-bone-soft focus:border-forest focus:outline-none text-ink placeholder:text-ink/40 text-sm"
              data-testid="monthly-email-input"
            />
            <input
              type="tel"
              placeholder="Phone (optional)"
              value={monthlyForm.phone}
              onChange={(e) => setMonthlyForm((s) => ({ ...s, phone: e.target.value }))}
              className="w-full h-12 px-4 rounded-xl bg-white border border-bone-soft focus:border-forest focus:outline-none text-ink placeholder:text-ink/40 text-sm"
              data-testid="monthly-phone-input"
            />
          </div>
          <DialogFooter className="pt-2 flex-col sm:flex-col gap-2">
            <Button
              onClick={submitMonthlyModal}
              disabled={loading}
              className="w-full bg-terracotta hover:bg-terracotta/90 text-bone rounded-full h-12 font-semibold text-sm"
              data-testid="monthly-dialog-submit"
            >
              Continue to secure payment
            </Button>
            <p className="text-[11px] text-ink/50 text-center">
              Cancel anytime · UPI Autopay by Razorpay
            </p>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Success Dialog */}
      <Dialog open={!!success} onOpenChange={(o) => !o && setSuccess(null)}>
        <DialogContent data-testid="give-success-dialog" className="bg-bone border-bone-soft max-w-md">
          <DialogHeader>
            <CheckCircle2 className="w-14 h-14 text-terracotta mb-2" strokeWidth={1.4}/>
            <DialogTitle className="font-serif text-3xl text-forest font-light">Thank you. Truly.</DialogTitle>
            <DialogDescription className="text-ink/80 text-base leading-relaxed">
              Because of you, <span className="text-forest font-semibold">₹{success?.amount?.toLocaleString("en-IN")}</span>{success?.mock ? " (test mode)" : ""} worth of food, medicine, and shelter is going to a dog who really needed it today. You've made this possible. 🐾
            </DialogDescription>
          </DialogHeader>
          <div className="pt-2 space-y-2.5">
            <a
              href="https://chat.whatsapp.com/KTPYodo4NTV1xwPCBgRcFt"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center justify-center gap-2.5 w-full bg-forest hover:bg-forest/90 text-bone rounded-full h-12 px-6 font-semibold text-sm tracking-wide transition-colors"
              data-testid="give-thankyou-whatsapp"
            >
              Join our WhatsApp community
            </a>
            <p className="text-xs text-ink/60 leading-relaxed text-center px-2">
              Get behind-the-scenes rescue updates, photos of dogs your donation helps, and be the first to hear about urgent cases and shelter milestones.
            </p>
          </div>
          <DialogFooter>
            <Button onClick={() => setSuccess(null)} variant="ghost" className="text-ink/60 hover:text-forest rounded-full" data-testid="give-thankyou-close">Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ────── Inline reel player: autoplay muted + custom mute/unmute toggle ──────
function ReelPlayer({ src, testId }) {
  const videoRef = useRef(null);
  const [muted, setMuted] = useState(true);

  const toggleMute = () => {
    const v = videoRef.current;
    if (!v) return;
    const next = !muted;
    v.muted = next;
    // Attempt to unpause after user gesture in case autoplay was blocked
    if (!next) v.play().catch(() => {});
    setMuted(next);
  };

  // Pause when scrolled off-screen for perf & bandwidth
  useEffect(() => {
    const v = videoRef.current;
    if (!v || typeof IntersectionObserver === "undefined") return;
    const obs = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) v.play().catch(() => {});
          else v.pause();
        });
      },
      { threshold: 0.25 }
    );
    obs.observe(v);
    return () => obs.disconnect();
  }, []);

  return (
    <div
      className="relative aspect-[9/16] rounded-2xl overflow-hidden bg-black shadow-[0_18px_45px_rgba(30,59,39,0.20)] group"
      data-testid={testId}
    >
      <video
        ref={videoRef}
        src={src}
        muted={muted}
        loop
        playsInline
        autoPlay
        preload="metadata"
        className="w-full h-full object-cover"
      />
      <button
        type="button"
        onClick={toggleMute}
        aria-label={muted ? "Unmute video" : "Mute video"}
        className="absolute bottom-3 right-3 w-10 h-10 rounded-full bg-black/55 hover:bg-black/75 backdrop-blur-sm text-white flex items-center justify-center transition-all opacity-90 hover:opacity-100 hover:scale-105"
        data-testid={`${testId}-mute-toggle`}
      >
        {muted ? <VolumeX className="w-4 h-4" strokeWidth={2}/> : <Volume2 className="w-4 h-4" strokeWidth={2}/>}
      </button>
    </div>
  );
}
